import type { CSSProperties } from 'react'
import { cn } from '@/shared/lib'
import { designTokens } from '@/shared/lib/design-tokens'

export type PlanPriceCardProps = {
  duration: string
  price: string
  pricePerMonth?: string
  trailingLabel?: string
  trailingLabelTone?: 'brand' | 'muted'
  variant?: 'desktop' | 'mobile'
  selected?: boolean
  disabled?: boolean
  showPricePerMonth?: boolean
  onSelect?: () => void
  className?: string
}

type TypographyToken = {
  size: string
  lineHeight: string
  weight: number
  letterSpacing?: string
}

const typographyStyle = (token: TypographyToken): CSSProperties => ({
  fontFamily: designTokens.typography.fontFamilies.primary,
  fontSize: token.size,
  lineHeight: token.lineHeight,
  fontWeight: token.weight,
  letterSpacing: token.letterSpacing,
})

export function PlanPriceCard({
  duration,
  price,
  pricePerMonth,
  trailingLabel,
  trailingLabelTone = 'brand',
  variant = 'desktop',
  selected = false,
  disabled = false,
  showPricePerMonth = true,
  onSelect,
  className,
}: PlanPriceCardProps) {
  const isMobile = variant === 'mobile'
  const borderColor = selected
    ? designTokens.colors.border.paymentSelected
    : designTokens.colors.border.paymentDefault
  const boxShadow = selected ? designTokens.shadows.paymentOptionSelected : 'none'
  const typographyTokens = isMobile
    ? designTokens.typography.priceCardMobile
    : designTokens.typography.priceCard

  const cardStyle: CSSProperties = {
    borderRadius: designTokens.borders.radius.lg,
    borderWidth: designTokens.borders.widths.thin,
    borderStyle: designTokens.borders.styles.solid,
    borderColor,
    background: designTokens.colors.background.surface,
    boxShadow,
    padding: isMobile
      ? `${designTokens.spacing.selectPriceCardPaddingYMobile} ${designTokens.spacing.selectPriceCardPaddingXMobile}`
      : designTokens.spacing.lg,
    minHeight: isMobile
      ? designTokens.spacing.selectPriceCardHeightMobile
      : designTokens.spacing.selectPriceCardHeight,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  }

  return (
    <button
      type="button"
      className={cn('w-full text-left transition-colors', className)}
      style={cardStyle}
      onClick={onSelect}
      aria-pressed={selected}
      disabled={disabled}
    >
      <div
        className="flex w-full flex-col"
        style={{
          gap: isMobile
            ? designTokens.spacing.selectPriceCardGapMobile
            : designTokens.spacing.selectPriceCardGap,
        }}
      >
        <div className="flex items-start justify-between" style={{ gap: designTokens.spacing.lg }}>
          <div
            style={{
              ...typographyStyle(typographyTokens.label),
              color: designTokens.colors.text.graphite,
            }}
          >
            {duration}
          </div>
          <div
            style={{
              ...typographyStyle(typographyTokens.price),
              color: designTokens.colors.text.graphite,
            }}
          >
            {price}
          </div>
        </div>
        <div className="flex items-start justify-between gap-4">
          {pricePerMonth && showPricePerMonth ? (
            <div
              style={{
                ...typographyStyle(typographyTokens.monthly),
                color: designTokens.colors.text.primary,
              }}
            >
              {pricePerMonth}
            </div>
          ) : (
            <span />
          )}
          {trailingLabel ? (
            <span
              style={{
                ...typographyStyle(typographyTokens.benefit),
                color:
                  trailingLabelTone === 'brand'
                    ? designTokens.colors.brand.primary
                    : designTokens.colors.text.secondary,
                textTransform: trailingLabelTone === 'brand' ? 'uppercase' : 'none',
              }}
            >
              {trailingLabel}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  )
}
